import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type { AdminUser, LinkEmail, LinkEmailResponse, JobStarted, JobStatus, UserDetailResponse, DeleteAddressResponse, SignedCountResponse, DuplicateEmailsResponse, DedupEmailsResponse, GenerateEvidencePacket, BulkEvidencePacket, EvidencePacketSpec, QueryCaseSteps, QueryCaseStepsResponse, PatchCaseStepsResponse, UnlinkEmailResponse, PatchStepItem, SyncCaseJurisdictionResponse, AdminUserPaymentsResponse, PartnerStateResetResponse, PartnerIneligibleResponse, PartnerIneligibleState, AdminPartnerLead, AdminPartnerLeadLookupResponse } from '@kin/property-tax-types';

export type { AdminUser, LinkEmail, LinkEmailResponse, JobStarted, JobStatus, UserDetailResponse, DeleteAddressResponse, SignedCountResponse, DuplicateEmailsResponse, DedupEmailsResponse, GenerateEvidencePacket, BulkEvidencePacket, EvidencePacketSpec, QueryCaseSteps, QueryCaseStepsResponse, PatchCaseStepsResponse, UnlinkEmailResponse, PatchStepItem, SyncCaseJurisdictionResponse, AdminUserPaymentsResponse, PartnerStateResetResponse, PartnerIneligibleResponse, PartnerIneligibleState, AdminPartnerLead, AdminPartnerLeadLookupResponse };

export interface DataSourceOperation {
  supported: boolean;
  accept?: string[];
  params?: string[];
  paramEnums?: Record<string, string[]>;
}

export interface DataSource {
  id: string;
  label: string;
  description: string;
  upload: DataSourceOperation;
  ingest: DataSourceOperation;
}

export interface DataIngest {
  type: string;
  year?: string;
  tables?: string[];
  uploadJobId?: string;
  parcelsUploadJobId?: string;
}

export interface FixedWidthColumn {
  name: string;
  start: number;
  length: number;
}

export interface CountySourceFileConfig {
  fileName: string;
  dataLocationUrl?: string | null;
  delimiter: 'comma' | 'pipe' | 'tab' | 'fixedWidth';
  columns: string[] | FixedWidthColumn[];
}

export interface ApplyCountyLayoutResponse {
  applied: string[];
  unmatched: string[];
}

export interface CountyFieldMappingConfig {
  kinField: string;
  transformType: string;
  params: string[];
}

export interface CountyIngestConfigResponse {
  jurisdictionCode: string;
  state: string | null;
  stagedS3Prefix: string | null;
  stagedLocalPath: string | null;
  sourceFiles: CountySourceFileConfig[];
  fieldMappings: CountyFieldMappingConfig[];
}

export interface CountyIngestConfigSummary {
  jurisdictionCode: string;
  state: string | null;
}

@Injectable({ providedIn: 'root' })
export class AdminService {
  private http = inject(HttpClient);

  listUsers(): Observable<AdminUser[]> {
    return this.http.get<AdminUser[]>(`${environment.apiUrl}/admin/users`);
  }

  promoteUser(email: string): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/promote`, {});
  }

  demoteUser(email: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/promote`);
  }

  linkEmail(payload: LinkEmail): Observable<LinkEmailResponse> {
    return this.http.post<LinkEmailResponse>(`${environment.apiUrl}/admin/link-email`, payload);
  }

  unlinkEmail(email: string, county: LinkEmail['county'], parcelId: string): Observable<UnlinkEmailResponse> {
    return this.http.delete<UnlinkEmailResponse>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/link/${encodeURIComponent(county)}/${encodeURIComponent(parcelId)}`);
  }

  // Deliberately a separate call from linkEmail — the association itself must succeed
  // or fail on its own, independent of whether the user's case gets re-pointed at it.
  // See admin.component.ts's submitLinkEmail for the retry/revert orchestration.
  syncCaseJurisdiction(email: string): Observable<SyncCaseJurisdictionResponse> {
    return this.http.patch<SyncCaseJurisdictionResponse>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/case-jurisdiction`, {});
  }

  deleteSignatureRequests(email: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/signature-requests`);
  }

  getUser(email: string): Observable<UserDetailResponse> {
    return this.http.get<UserDetailResponse>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}`);
  }

  deleteAddress(email: string, addressId: string): Observable<DeleteAddressResponse> {
    return this.http.delete<DeleteAddressResponse>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/addresses/${encodeURIComponent(addressId)}`);
  }

  getUserPayments(email: string): Observable<AdminUserPaymentsResponse> {
    return this.http.get<AdminUserPaymentsResponse>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/payments`);
  }

  // Reconciles pending rows against Stripe, deletes only genuinely-unsettled ones and clears
  // partnerAgreedAt. Never touches paid/refunded rows — resetting a paid user means refunding
  // in Stripe first, which the charge.refunded webhook already handles.
  resetPartnerState(email: string): Observable<PartnerStateResetResponse> {
    return this.http.delete<PartnerStateResetResponse>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/partner-state`);
  }

  // Reads the flag without changing it. `partnerIneligibleAt: null` means not marked — a real
  // answer, not an error; only an unknown email 404s. There is no boolean and no `changed`
  // here: null-ness is the state.
  getPartnerIneligible(email: string): Observable<PartnerIneligibleState> {
    return this.http.get<PartnerIneligibleState>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/partner-ineligible`);
  }

  // Marks the user as no longer eligible for the partner appeal, which is all this does:
  // it stamps one timestamp and touches no payment row. It issues no refund — refunds happen
  // in Stripe and arrive here via the charge.refunded webhook. A repeat call is a successful
  // no-op that returns the ORIGINAL timestamp with `changed: false`.
  markPartnerIneligible(email: string): Observable<PartnerIneligibleResponse> {
    return this.http.post<PartnerIneligibleResponse>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/partner-ineligible`, {});
  }

  // Reverses the above, because marking the wrong person happens. Clearing an already-clear
  // user is likewise a no-op with `changed: false`.
  clearPartnerIneligible(email: string): Observable<PartnerIneligibleResponse> {
    return this.http.delete<PartnerIneligibleResponse>(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/partner-ineligible`);
  }

  // Reads partner_leads by email, independently of whether a `users` row exists — importing a
  // lead creates no user, so this deliberately lives outside /admin/users. `found: false` is a
  // 200, not a 404: "no lead" is the answer this tool exists to give.
  getPartnerLead(email: string): Observable<AdminPartnerLeadLookupResponse> {
    return this.http.get<AdminPartnerLeadLookupResponse>(`${environment.apiUrl}/admin/partner-leads/${encodeURIComponent(email)}`);
  }

  getSignedCount(): Observable<SignedCountResponse> {
    return this.http.get<SignedCountResponse>(`${environment.apiUrl}/admin/users/signed-count`);
  }

  listDataSources(): Observable<DataSource[]> {
    return this.http.get<DataSource[]>(`${environment.apiUrl}/admin/data/sources`);
  }

  uploadData(file: File, type: string, year?: string, county?: string): Observable<JobStarted> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('type', type);
    if (year) {
      formData.append('year', year);
    }
    if (county) {
      formData.append('county', county);
    }
    return this.http.post<JobStarted>(`${environment.apiUrl}/admin/upload`, formData);
  }

  ingestData(payload: DataIngest): Observable<JobStarted> {
    return this.http.post<JobStarted>(`${environment.apiUrl}/admin/ingest`, payload);
  }

  uploadCountyData(file: File, county: string): Observable<JobStarted> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('county', county);
    return this.http.post<JobStarted>(`${environment.apiUrl}/admin/county-data/upload`, formData);
  }

  listJobs(): Observable<JobStatus[]> {
    return this.http.get<JobStatus[]>(`${environment.apiUrl}/admin/jobs`);
  }

  getJobStatus(jobId: string): Observable<JobStatus> {
    return this.http.get<JobStatus>(`${environment.apiUrl}/admin/jobs/${encodeURIComponent(jobId)}`);
  }

  cancelJob(jobId: string): Observable<JobStatus> {
    return this.http.post<JobStatus>(`${environment.apiUrl}/admin/jobs/${encodeURIComponent(jobId)}/cancel`, {});
  }

  downloadUserDocuments(email: string, onError?: () => void): void {
    this.http.get(`${environment.apiUrl}/admin/users/${encodeURIComponent(email)}/download-documents`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${email}-documents.zip`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => onError?.(),
    });
  }

  getDuplicateEmails(): Observable<DuplicateEmailsResponse> {
    return this.http.get<DuplicateEmailsResponse>(`${environment.apiUrl}/admin/users/duplicate-emails`);
  }

  deduplicateEmails(): Observable<DedupEmailsResponse> {
    return this.http.post<DedupEmailsResponse>(`${environment.apiUrl}/admin/users/deduplicate-emails`, {});
  }

  generateEvidencePacket(payload: GenerateEvidencePacket): Observable<Blob> {
    return this.http.post(`${environment.apiUrl}/admin/evidence-packet`, payload, { responseType: 'blob' });
  }

  startEvidencePacketBatch(payload: BulkEvidencePacket): Observable<JobStarted> {
    return this.http.post<JobStarted>(`${environment.apiUrl}/admin/evidence-packets/batch`, payload);
  }

  downloadEvidencePacketBatch(jobId: string): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/admin/evidence-packets/batch/${encodeURIComponent(jobId)}/download`, { responseType: 'blob' });
  }

  queryCaseSteps(properties: QueryCaseSteps['properties']): Observable<QueryCaseStepsResponse> {
    const body: QueryCaseSteps = { properties };
    return this.http.post<QueryCaseStepsResponse>(`${environment.apiUrl}/admin/case/steps/query`, body);
  }

  patchCaseSteps(steps: PatchStepItem[]): Observable<PatchCaseStepsResponse> {
    return this.http.patch<PatchCaseStepsResponse>(`${environment.apiUrl}/admin/case/steps`, { steps });
  }

  listCountyIngestConfigs(): Observable<CountyIngestConfigSummary[]> {
    return this.http.get<CountyIngestConfigSummary[]>(`${environment.apiUrl}/admin/county-ingest-config`);
  }

  getCountyIngestConfig(jurisdictionCode: string): Observable<CountyIngestConfigResponse> {
    return this.http.get<CountyIngestConfigResponse>(`${environment.apiUrl}/admin/county-ingest-config/${encodeURIComponent(jurisdictionCode)}`);
  }

  saveCountyIngestConfig(jurisdictionCode: string, payload: Omit<CountyIngestConfigResponse, 'jurisdictionCode' | 'stagedS3Prefix' | 'stagedLocalPath'>): Observable<CountyIngestConfigResponse> {
    return this.http.put<CountyIngestConfigResponse>(`${environment.apiUrl}/admin/county-ingest-config/${encodeURIComponent(jurisdictionCode)}`, payload);
  }

  fetchCountyIngestConfig(jurisdictionCode: string, url?: string): Observable<JobStarted> {
    return this.http.post<JobStarted>(`${environment.apiUrl}/admin/county-ingest-config/${encodeURIComponent(jurisdictionCode)}/fetch`, { url });
  }

  ingestCountyIngestConfig(jurisdictionCode: string): Observable<JobStarted> {
    return this.http.post<JobStarted>(`${environment.apiUrl}/admin/county-ingest-config/${encodeURIComponent(jurisdictionCode)}/ingest`, {});
  }

  applyCountyLayout(jurisdictionCode: string, file: File): Observable<ApplyCountyLayoutResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<ApplyCountyLayoutResponse>(`${environment.apiUrl}/admin/county-ingest-config/${encodeURIComponent(jurisdictionCode)}/layout`, formData);
  }

  downloadAllDocuments(onError?: () => void): void {
    this.http.get(`${environment.apiUrl}/admin/download-documents`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'documents.zip';
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => onError?.(),
    });
  }
}
